import type { RouterOutputs } from "@/lib/trpc";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { dayStatusLabel, type DayStatus } from "@shared/trustLabels";

/**
 * "This Week" work-week strip. Renders the SERVER-classified days from
 * `receipts.weekStatus` verbatim — it performs NO date bucketing, NO timezone
 * classification, NO status calculation. The server already decided each day's
 * `isWorkingDay` and `status` (local-tz, A6/A9); this component only maps the
 * status enum to a colour.
 *
 * Layout (Option A): a compact 7-day glance — weekday · date · status dot. The
 * legend below is the key (a per-cell word doesn't fit on a phone). Today's cell
 * is highlighted.
 */
type WeekStatus = RouterOutputs["receipts"]["weekStatus"];
type DayCell = WeekStatus["days"][number];

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_AR = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

/** Dot colour per status — presentation only. */
const DOT: Record<DayStatus, string> = {
  draft: "bg-sky-500",
  update_available: "bg-amber-500",
  needs_update: "bg-orange-500",
  retracted: "bg-slate-400",
  not_started: "bg-muted-foreground/40",
  ready: "bg-violet-500",
  approved: "bg-emerald-500",
  weekend_note: "bg-coral",
  hidden: "bg-transparent",
};

function fmtDate(dateKey: string, isAr?: boolean): string {
  // Display-only formatting of a server-provided date string (UTC to match
  // the server's classification — NOT a client timezone decision).
  return new Date(dateKey + "T00:00:00.000Z").toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function Day({
  cell,
  isToday,
  onClick,
  isAr,
}: {
  cell: DayCell;
  isToday: boolean;
  onClick?: () => void;
  isAr?: boolean;
}) {
  const isRest = !cell.isWorkingDay;
  const status = cell.status as DayStatus;
  const isWeekendNote = isRest && status === "weekend_note";
  const isEmptyWeekend = isRest && status !== "weekend_note";
  // Empty rest days have nothing to open; everything else is tappable when a
  // handler is provided.
  const interactive = !!onClick && !isEmptyWeekend;

  const className = cn(
    "flex flex-1 min-w-0 flex-col items-center gap-2 rounded-2xl border px-1 py-3 text-center",
    isToday
      ? "border-coral bg-coral-soft/25"
      : isWeekendNote
        ? "border-coral/30 bg-coral-soft/15"
        : "border-border/60",
    isEmptyWeekend && !isToday ? "opacity-60" : "",
    interactive ? "transition-colors hover:bg-muted/40 active:scale-95" : "",
  );

  const inner = (
    <>
      <div
        className={cn(
          "text-[13px] font-semibold",
          isToday ? "text-coral" : isRest ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {(isAr ? WD_AR : WD)[cell.weekdayIndex]}
      </div>
      <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(cell.dateKey, isAr)}</div>
      {isEmptyWeekend ? (
        <span className="h-2.5 w-2.5 rounded-full border-2 border-muted-foreground/40" />
      ) : isWeekendNote ? (
        // HOLLOW coral ring — must match the legend's "Weekend note" marker.
        // A solid coral dot here was indistinguishable from the solid orange
        // "Changes not shared" dot at this size.
        <span
          className="h-2.5 w-2.5 rounded-full border-2 border-coral"
          data-testid="weekend-note-dot"
        />
      ) : (
        <span className={cn("h-2.5 w-2.5 rounded-full", DOT[status])} />
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={
          isAr
            ? `فتح ${WD_AR[cell.weekdayIndex]} ${fmtDate(cell.dateKey, isAr)}`
            : `Open ${WD[cell.weekdayIndex]} ${fmtDate(cell.dateKey)}`
        }
        className={className}
        data-testid="weekstrip-day"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={className} data-testid="weekstrip-day">
      {inner}
    </div>
  );
}

/** Server-derived work-week range label, e.g. "Sun – Thu" — presentation only. */
function workWeekLabel(days: WeekStatus["days"], isAr?: boolean): string | null {
  const work = days.filter(d => d.isWorkingDay);
  if (work.length === 0) return null;
  const wd = isAr ? WD_AR : WD;
  return `${wd[work[0].weekdayIndex]} – ${wd[work[work.length - 1].weekdayIndex]}`;
}

const LEGEND: { status: DayStatus; label: string }[] = [
  { status: "draft", label: "Draft" },
  { status: "ready", label: "Ready" },
  { status: "approved", label: "Approved" },
  { status: "update_available", label: "New updates" },
  { status: "needs_update", label: "Changes not shared" },
  { status: "retracted", label: "Retracted" },
  { status: "not_started", label: "Not started" },
  { status: "weekend_note", label: "Weekend note" },
];

export function WorkWeekStrip({
  weekStatus,
  label = "This week",
  todayKey,
  onDayClick,
}: {
  weekStatus: WeekStatus;
  /** Week-context label (e.g. "This week" / "Last week" / "Week of May 10"). */
  label?: string;
  /** Local YYYY-MM-DD for "today" — highlights that cell when in view. */
  todayKey?: string;
  /** When provided, day cells become tappable (open that day). */
  onDayClick?: (dateKey: string) => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const range = workWeekLabel(weekStatus.days, isAr);
  // Show working days always; weekend days only when they actually carry a note.
  // Empty weekends are hidden (and the remaining cells flex to fill the row).
  const visibleDays = weekStatus.days.filter(
    d => d.isWorkingDay || d.status === "weekend_note",
  );
  return (
    <section className="rounded-3xl border border-border/60 bg-card p-4 shadow-sm" data-testid="workweek-strip">
      <div className="mb-3 flex items-center gap-1.5 px-1">
        <div className="text-[11px] font-semibold tracking-wider text-muted-foreground">
          <span className="uppercase">{label}</span>
          {range ? (
            <span className="ms-1 font-normal normal-case tracking-normal text-muted-foreground/80">
              {isAr ? <>(أيام العمل: {range})</> : <>(Work week: {range})</>}
            </span>
          ) : null}
        </div>
        <Info className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
      </div>

      <div className="flex gap-1.5">
        {visibleDays.map(d => (
          <Day
            key={d.dateKey}
            cell={d}
            isToday={!!todayKey && d.dateKey === todayKey}
            onClick={onDayClick ? () => onDayClick(d.dateKey) : undefined}
            isAr={isAr}
          />
        ))}
      </div>

      {/* Status legend — the key for the dots above. */}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 px-1">
        {LEGEND.map(l => {
          const isNote = l.status === "weekend_note";
          return (
            <span key={l.status} className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
              {isNote ? (
                <span className="h-1.5 w-1.5 rounded-full border-2 border-coral" />
              ) : (
                <span className={cn("h-1.5 w-1.5 rounded-full", DOT[l.status])} />
              )}
              {isAr ? dayStatusLabel(l.status, locale) : l.label}
            </span>
          );
        })}
      </div>
    </section>
  );
}
